import React, { createContext, useEffect, useState } from 'react'
import { safeTgtLogger } from '../utils/safeTgtLogger'
import Firebase from 'firebase/compat/app'
import 'firebase/compat/analytics'
import 'firebase/compat/remote-config'
import { setCustomSignals } from 'firebase/remote-config'
import { useMPMEnv } from 'utils/env'
import { useEssentials } from 'hooks/utils/useEssentials'
import { Constants } from 'constants/constants'
import { LoginPlaceholder } from 'components/login-placeholder/loginPlaceholder'
import { getDeviceCategory } from 'utils/helpers'
import { defaultFeatureFlags } from './defaultFeatureFlags'

export { defaultFeatureFlags }

type FeatureFlags = Record<string, boolean>

// Set by acceptance tests (via addInitScript) to exercise flag-off code paths,
// since E2E bypasses the Remote Config fetch entirely.
declare global {
    interface Window {
        __E2E_FEATURE_FLAGS__?: FeatureFlags
    }
}

// Safe fallback when Firebase fetch fails — all flags on except restricted features
const errorFallbackFlags: FeatureFlags = Object.fromEntries(
    Object.keys(defaultFeatureFlags).map((key) => [
        key,
        key === 'insightsEnabled' || key === 'sortByTimeEnabled' ? false : true,
    ]),
)

export const BannerKey = 'banners'

export interface BannerConfigItem {
    pageUrl: string
    title: string
    message: string
    startsAtUTC: string
    expiresAtUTC: string
    linkUrl?: string
    linkText?: string
}

interface RemoteConfigContextType {
    featureFlags: Record<string, boolean>
    bannerConfig: BannerConfigItem[]
    isLoading: boolean
}

export const RemoteConfigContext = createContext<RemoteConfigContextType>({
    featureFlags: defaultFeatureFlags,
    bannerConfig: [],
    isLoading: true,
})

type FlagsType = {
    children: React.ReactNode
}

export const FlagsProvider = ({ children }: FlagsType) => {
    const {
        greenfieldGroups,
        userLocationHierarchy,
        storeLocationDetails,
        session,
    } = useEssentials()

    const lanId = session?.userInfo?.lanId ?? ''
    const userLoginId = lanId.trim().toUpperCase()
    const { firebase: firebaseConfig, environment } = useMPMEnv()
    const [flags, setFlags] =
        useState<Record<string, boolean>>(defaultFeatureFlags)
    const [bannerConfig, setBannerConfig] = useState<BannerConfigItem[]>([])
    const [isLoading, setIsLoading] = useState(true)

    // Get current app version
    const appVersion = process.env.REACT_APP_VERSION ?? ''

    useEffect(() => {
        // Use default flags for test environments only
        if (process.env.CI || process.env.REACT_APP_E2E === 'true') {
            setBannerConfig([])
            // Allow E2E tests to override individual flags (e.g. sortByTimeEnabled)
            // via window.__E2E_FEATURE_FLAGS__ injected by the Playwright setup helpers.
            setFlags({
                ...defaultFeatureFlags,
                ...window.__E2E_FEATURE_FLAGS__,
            })
            setIsLoading(false)
            return
        }

        // If the session is loaded but the user has a blank LAN ID,
        // skip Remote Config and proceed with error fallback flags.
        // The router's access checks will handle showing the Unauthorized page.
        if (userLoginId === '' && session) {
            setFlags(errorFallbackFlags)
            setIsLoading(false)
            return
        }

        // Don't fetch Remote Config yet if session hasn't loaded.
        if (userLoginId === '') {
            setIsLoading(true)
            return
        }

        const app = Firebase.apps.length
            ? Firebase.app()
            : Firebase.initializeApp(firebaseConfig)
        const analytics = app.analytics()
        const remoteConfig = app.remoteConfig()

        // Cache fetched config for 60s to align with the banner poll interval.
        // Allow up to 15s for the fetch before timing out — 5s was too tight for
        // mobile devices on store WiFi and was causing widespread false failures.
        remoteConfig.settings = {
            minimumFetchIntervalMillis: 60000,
            fetchTimeoutMillis: 15000,
        }

        analytics.setUserProperties({
            device_type: getDeviceCategory(),
            user_env: environment,
            user_login_id: userLoginId,
        })

        if (greenfieldGroups) {
            const { ADMIN_PROD, BETA } = Constants.GREENFIELD_GROUPS
            const filteredBetaGroups = greenfieldGroups.filter((group) =>
                [ADMIN_PROD, BETA].includes(group),
            )

            analytics.setUserProperties({
                beta_user_group: filteredBetaGroups.join(','),
                beta_district_location: userLocationHierarchy.district_id,
                user_region_id: userLocationHierarchy.region_id,
                user_group_id: userLocationHierarchy.group_id,
                user_district_id: userLocationHierarchy.district_id,
                user_store_id: userLocationHierarchy.store_id,
            })
        }

        // 1) initial load flags + banners
        const loadAll = async () => {
            try {
                // Set custom signals before fetching so targeting conditions can match
                await setCustomSignals(remoteConfig, {
                    user_login_id: userLoginId,
                })

                await remoteConfig.fetchAndActivate()

                const remoteFeatureFlags = Object.fromEntries(
                    Object.entries(remoteConfig.getAll()).map(
                        ([key, value]) => [key, value.asBoolean()],
                    ),
                )

                const featureFlags: FeatureFlags = {
                    ...defaultFeatureFlags,
                    ...remoteFeatureFlags,
                }

                // Pilot store rollout: If Firebase conditions (HQ Users, Beta groups, etc.)
                // already granted access, skip pilot check. Otherwise, check if user has
                // access to any store in the pilot list.
                // To roll out to full chain: set `myTargetPerformancePilotStores` to ""
                // in Firebase — the flag falls through to whatever `myTargetPerformanceEnabled`
                // is set to in Firebase (typically true for all).
                if (!featureFlags.myTargetPerformanceEnabled) {
                    const pilotStoresRaw = remoteConfig
                        .getValue('myTargetPerformancePilotStores')
                        .asString()
                        .trim()

                    if (pilotStoresRaw.length > 0) {
                        // Don't evaluate pilot stores until user data has loaded
                        // This prevents us from incorrectly denying access on first render
                        // when storeLocationDetails is still an empty array
                        if (storeLocationDetails.length === 0) {
                            // User data not loaded yet - keep loading screen up
                            // Effect will re-run when storeLocationDetails populates
                            return
                        }

                        const pilotStoreIds = new Set(
                            pilotStoresRaw
                                .split(',')
                                .map((s) => s.trim())
                                .filter((s) => s.length > 0)
                                .map(Number)
                                .filter((n) => !isNaN(n)),
                        )
                        // Check if user has access to any pilot store in their accessible stores list
                        featureFlags.myTargetPerformanceEnabled =
                            storeLocationDetails.some((store) =>
                                pilotStoreIds.has(store.id),
                            )
                    }
                }

                setFlags(featureFlags)
                setIsLoading(false)

                const raw = remoteConfig.getValue(BannerKey).asString()
                const bannerArray = JSON.parse(raw) as BannerConfigItem[]
                setBannerConfig(bannerArray)
            } catch (error) {
                const isTimeout =
                    error instanceof Error &&
                    error.message.toLowerCase().includes('timeout')
                safeTgtLogger.warn('Error during Remote Config initial fetch', {
                    event: {
                        action: 'firebase_config_fetch_failed',
                        category: 'firebase',
                        outcome: 'failure',
                    },
                    error: {
                        message:
                            error instanceof Error
                                ? error.message
                                : 'Unknown error',
                        type:
                            error instanceof Error
                                ? error.name
                                : 'UnknownError',
                    },
                    nonIndexedFields: {
                        is_timeout: isTimeout,
                        firebase_app_count: Firebase.apps.length,
                    },
                })
                setFlags(errorFallbackFlags)
                setIsLoading(false)
            }
        }

        // 2) poll banners every 60s
        const pollBanners = async () => {
            try {
                await setCustomSignals(remoteConfig, {
                    user_login_id: userLoginId,
                })

                await remoteConfig.fetchAndActivate()

                const raw = remoteConfig.getValue(BannerKey).asString()
                const bannerArray = JSON.parse(raw) as BannerConfigItem[]
                setBannerConfig(bannerArray)
            } catch (error) {
                const isTimeout =
                    error instanceof Error &&
                    error.message.toLowerCase().includes('timeout')
                safeTgtLogger.warn('Error polling banner configs', {
                    event: {
                        action: 'firebase_banner_poll_failed',
                        category: 'firebase',
                        outcome: 'failure',
                    },
                    error: {
                        message:
                            error instanceof Error
                                ? error.message
                                : 'Unknown error',
                        type:
                            error instanceof Error
                                ? error.name
                                : 'UnknownError',
                    },
                    nonIndexedFields: {
                        is_timeout: isTimeout,
                    },
                })
            }
        }

        void loadAll()
        // set up interval to poll banners every 60 seconds
        const intervalId = setInterval(pollBanners, 60000)
        return () => clearInterval(intervalId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        firebaseConfig,
        environment,
        greenfieldGroups,
        appVersion,
        storeLocationDetails,
        userLoginId,
    ])

    if (isLoading) {
        return <LoginPlaceholder />
    }

    return (
        <RemoteConfigContext.Provider
            value={{ featureFlags: flags, bannerConfig, isLoading }}
        >
            {children}
        </RemoteConfigContext.Provider>
    )
}

export const useFlags = () => React.useContext(RemoteConfigContext).featureFlags

export default FlagsProvider
